/**
 * Row flattening and dropdown options for the Resource/Action/Role policy pages.
 * Each GET-all response is nested; these turn it into one row per principal and resource
 * (actions grouped into one cell) or per user, role and resource.
 */
import Badge from '@cloudscape-design/components/badge';
import SpaceBetween from '@cloudscape-design/components/space-between';
import type { SelectProps } from '@cloudscape-design/components/select';
import type { ResultColumn } from '../components/ResultViewer';
import { JsonObject, isObject, resourcePaths } from '../components/resultFormat';

type Obj = Record<string, unknown>;
const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const objs = (v: unknown): Obj[] => (Array.isArray(v) ? v.filter(isObject) : []);

/** A resource exactly as the API returned it: `{ name, scopes?, resources?: [...] }`. */
export type ResourceJson = JsonObject;

/** "yeti › bom [US]" for a resource chain (several paths are joined if it branches). */
export function resourceText(resource: unknown): string | null {
  return isObject(resource) && typeof resource.name === 'string' ? resourcePaths(resource).join(', ') : null;
}

// ---------- dropdown options ----------

interface Named {
  name: string;
  display_name?: string | null;
  description?: string | null;
}

/** Options for named entities (user groups, roles, actions): name as label, display name and description below. */
export function toNamedOptions(items: unknown): SelectProps.Option[] {
  if (!Array.isArray(items)) return [];
  return (items as Named[])
    .filter(g => typeof g?.name === 'string' && g.name !== '')
    .map(g => ({
      value: g.name,
      label: g.name,
      description: g.display_name || undefined,
      tags: g.description ? [g.description] : undefined,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export interface ResourceChoice {
  /** Path label, also used as the option value. */
  path: string;
  /** The chain from the root down to this node, ready to send to the API. */
  resource: ResourceJson;
}

/**
 * Every node of the `/resources` tree as a choosable chain. Picking "yeti › bom" sends
 * `{ name: 'yeti', resources: [{ name: 'bom' }] }`, the same shape the policy APIs return.
 */
export function toResourceChoices(tree: unknown): ResourceChoice[] {
  const out: ResourceChoice[] = [];
  const walk = (node: Obj, ancestors: Obj[]) => {
    if (typeof node.name !== 'string' || node.name === '') return;
    const scopes = Array.isArray(node.scopes) ? node.scopes.filter(s => typeof s === 'string') : [];
    const self: Obj = { name: node.name, ...(scopes.length ? { scopes } : {}) };
    const lineage = [...ancestors, self];
    // Rebuild the chain root-first, each level holding only the next level.
    const chain = lineage.reduceRight<Obj | null>(
      (child, level) => ({ ...level, ...(child ? { resources: [child] } : {}) }),
      null
    ) as ResourceJson;
    out.push({ path: resourcePaths(chain).join(', '), resource: chain });
    for (const child of objs(node.resources ?? node.children)) walk(child, lineage);
  };
  for (const root of objs(tree)) walk(root, []);
  return out.sort((a, b) => a.path.localeCompare(b.path));
}

export const resourceOptions = (choices: ResourceChoice[]): SelectProps.Option[] =>
  choices.map(c => ({ value: c.path, label: c.path }));

// ---------- User - UserGroup ----------

export interface UserUserGroupRow {
  userType: string | null;
  user: string | null;
  userGroup: string | null;
  displayName: string | null;
  description: string | null;
}

/** `[{ user: { type, value }, user_group: { name, display_name, description } }]` -> one row per policy. */
export function toUserUserGroupRows(data: unknown): UserUserGroupRow[] | null {
  if (!Array.isArray(data) || !data.every(isObject)) return null;
  return data.map(p => {
    const user = isObject(p.user) ? p.user : {};
    const group = isObject(p.user_group) ? p.user_group : {};
    return {
      userType: str(user.type),
      user: str(user.value),
      userGroup: str(group.name),
      displayName: str(group.display_name),
      description: str(group.description),
    };
  });
}

export const USER_USER_GROUP_COLUMNS: ResultColumn<UserUserGroupRow>[] = [
  { id: 'userType', header: 'User Type', value: r => r.userType },
  { id: 'user', header: 'User', value: r => r.user },
  { id: 'userGroup', header: 'User Group', value: r => r.userGroup },
  { id: 'displayName', header: 'Display Name', value: r => r.displayName },
  { id: 'description', header: 'Description', value: r => r.description },
];

// ---------- User - Resource - Action ----------

export interface UserResourceActionRow {
  userType: string | null;
  user: string | null;
  resourcePath: string | null;
  resource: ResourceJson | null;
  actions: string[];
}

const actionNames = (v: unknown): string[] =>
  objs(v)
    .map(a => str(a.name))
    .filter((n): n is string => n !== null)
    .sort((a, b) => a.localeCompare(b));

/** The actions of a row as badges; `value` (space-joined names) drives filtering and sorting. */
function actionsColumn<T extends { actions: string[] }>(): ResultColumn<T> {
  return {
    id: 'grantedActions',
    header: 'Actions',
    value: r => r.actions.join(' '),
    cell: r =>
      r.actions.length ? (
        <SpaceBetween direction="horizontal" size="xxs">
          {r.actions.map(a => (
            <Badge key={a}>{a}</Badge>
          ))}
        </SpaceBetween>
      ) : (
        '-'
      ),
  };
}

/** `[{ user, resources_with_actions: [{ resource, actions: [...] }] }]` -> one row per user and resource. */
export function toUserResourceActionRows(data: unknown): UserResourceActionRow[] | null {
  if (!Array.isArray(data) || !data.every(isObject)) return null;
  return data.flatMap(p => {
    const user = isObject(p.user) ? p.user : {};
    return objs(p.resources_with_actions).map(ra => ({
      userType: str(user.type),
      user: str(user.value),
      resourcePath: resourceText(ra.resource),
      resource: isObject(ra.resource) ? (ra.resource as ResourceJson) : null,
      actions: actionNames(ra.actions),
    }));
  });
}

export const USER_RESOURCE_ACTION_COLUMNS: ResultColumn<UserResourceActionRow>[] = [
  { id: 'userType', header: 'User Type', value: r => r.userType },
  { id: 'user', header: 'User', value: r => r.user },
  { id: 'resource', header: 'Resource', value: r => r.resourcePath },
  actionsColumn<UserResourceActionRow>(),
];

// ---------- UserGroup - Resource - Action ----------

export interface UserGroupResourceActionRow {
  userGroup: string | null;
  displayName: string | null;
  resourcePath: string | null;
  resource: ResourceJson | null;
  actions: string[];
}

/** `[{ user_group, resources_with_actions: [{ resource, actions: [...] }] }]` -> one row per group and resource. */
export function toUserGroupResourceActionRows(data: unknown): UserGroupResourceActionRow[] | null {
  if (!Array.isArray(data) || !data.every(isObject)) return null;
  return data.flatMap(p => {
    const group = isObject(p.user_group) ? p.user_group : {};
    return objs(p.resources_with_actions).map(ra => ({
      userGroup: str(group.name),
      displayName: str(group.display_name),
      resourcePath: resourceText(ra.resource),
      resource: isObject(ra.resource) ? (ra.resource as ResourceJson) : null,
      actions: actionNames(ra.actions),
    }));
  });
}

export const USER_GROUP_RESOURCE_ACTION_COLUMNS: ResultColumn<UserGroupResourceActionRow>[] = [
  { id: 'userGroup', header: 'User Group', value: r => r.userGroup },
  { id: 'displayName', header: 'Display Name', value: r => r.displayName },
  { id: 'resource', header: 'Resource', value: r => r.resourcePath },
  actionsColumn<UserGroupResourceActionRow>(),
];

// ---------- User - Role - Resource ----------

export interface UserRoleResourceRow {
  userType: string | null;
  user: string | null;
  role: string | null;
  resourcePath: string | null;
  resource: ResourceJson | null;
}

/**
 * `[{ user, rolesWithResources: [{ role, resources: [...] }] }]` -> one row per user, role and resource.
 * `rolesWithResources` really is camelCase: it is the field name of the service's UserRolesWithResources schema.
 */
export function toUserRoleResourceRows(data: unknown): UserRoleResourceRow[] | null {
  if (!Array.isArray(data) || !data.every(isObject)) return null;
  return data.flatMap(p => {
    const user = isObject(p.user) ? p.user : {};
    return objs(p.rolesWithResources).flatMap(rr => {
      const role = isObject(rr.role) ? rr.role : {};
      return objs(rr.resources).map(res => ({
        userType: str(user.type),
        user: str(user.value),
        role: str(role.name),
        resourcePath: resourceText(res),
        resource: res as ResourceJson,
      }));
    });
  });
}

export const USER_ROLE_RESOURCE_COLUMNS: ResultColumn<UserRoleResourceRow>[] = [
  { id: 'userType', header: 'User Type', value: r => r.userType },
  { id: 'user', header: 'User', value: r => r.user },
  { id: 'role', header: 'Role', value: r => r.role },
  { id: 'resource', header: 'Resource', value: r => r.resourcePath },
];

